import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase.ts';
import { isValidPhone, formatPhoneInput } from '../../lib/format.ts';

type LoginMode = 'email' | 'phone';

export function StaffLoginPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<LoginMode>('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      let result;
      if (mode === 'email') {
        result = await supabase.auth.signInWithPassword({ email, password });
      } else {
        const digits = phone.replace(/\D/g, '');
        const e164 = digits.startsWith('7') ? `+${digits}` : `+7${digits.slice(digits.startsWith('8') ? 1 : 0)}`;
        result = await supabase.auth.signInWithPassword({ phone: e164, password });
      }

      if (result.error) {
        if (result.error.message.includes('Invalid login')) {
          setError('Неверный логин или пароль');
        } else {
          setError(result.error.message);
        }
        return;
      }

      navigate('/staff', { replace: true });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="flex min-h-dvh flex-col items-center justify-center p-4"
      style={{ background: 'var(--bg)' }}
    >
      <div
        className="w-full max-w-sm rounded-2xl p-6"
        style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-md)' }}
      >
        <h1
          className="mb-1 text-xl font-semibold"
          style={{ color: 'var(--text)' }}
        >
          Вход для персонала
        </h1>
        <p className="mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>
          Используйте учётные данные, выданные владельцем
        </p>

        {/* Mode toggle */}
        <div
          className="mb-5 flex rounded-lg p-1"
          style={{ background: 'var(--n-100)' }}
          role="tablist"
        >
          {(['email', 'phone'] as const).map((m) => (
            <button
              key={m}
              role="tab"
              aria-selected={mode === m}
              onClick={() => { setMode(m); setError(null); }}
              className="flex-1 rounded-md py-1.5 text-sm font-medium transition-colors"
              style={{
                background: mode === m ? 'var(--surface)' : 'transparent',
                color: mode === m ? 'var(--text)' : 'var(--text-muted)',
                boxShadow: mode === m ? 'var(--shadow-sm)' : 'none',
              }}
            >
              {m === 'email' ? 'Email' : 'Телефон'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
          {mode === 'email' ? (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="email"
                className="text-sm font-medium"
                style={{ color: 'var(--text)' }}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors"
                style={{
                  background: 'var(--bg)',
                  color: 'var(--text)',
                  borderColor: 'var(--border)',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="phone"
                className="text-sm font-medium"
                style={{ color: 'var(--text)' }}
              >
                Телефон
              </label>
              <input
                id="phone"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                placeholder="+7 999 123-45-67"
                required
                className="rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors"
                style={{
                  background: 'var(--bg)',
                  color: 'var(--text)',
                  borderColor: 'var(--border)',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label
              htmlFor="password"
              className="text-sm font-medium"
              style={{ color: 'var(--text)' }}
            >
              Пароль
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors"
              style={{
                background: 'var(--bg)',
                color: 'var(--text)',
                borderColor: 'var(--border)',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            />
          </div>

          {error && (
            <p
              className="rounded-lg px-3 py-2 text-sm"
              role="alert"
              style={{ background: 'oklch(97% 0.015 25)', color: 'var(--danger)' }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading || (mode === 'phone' && !isValidPhone(phone))}
            className="mt-1 rounded-lg py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
            style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
          >
            {loading ? 'Вход…' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  );
}
